import styled from "styled-components";

import { getStaticUrl } from "js/config";
import { WorkspacePlanTier } from "common/interfaces";
import stripeProducts, { RecurringPlanDefinition } from "common/stripeProducts";
import getLogger, { LogGroup } from "js/core/logger";

const logger = getLogger(LogGroup.CHECKOUT);

export interface CheckoutProps {
    plan?: WorkspacePlanTier;
    enterpriseInviteId?: string;
    billingInterval?: "month" | "year";
    priceIds?: string[];
    displayName?: string;
    minQuantity?: number;
    workspaceId?: string;
    withImageBackground?: boolean;
}

export { default as ChoosePlan } from "./components/ChoosePlan/ChoosePlan";

export { default as ProCheckout, ProCheckoutContent, ProCheckoutDialog } from "./plans/pro/ProCheckout";
export { default as TeamCheckout, TeamCheckoutContent, TeamCheckoutDialog } from "./plans/team/TeamCheckout";
export { default as EnterpriseCheckout } from "./plans/enterprise/EnterpriseCheckout";

export const CheckoutContainer = styled.div<{ withImageBackground: boolean }>`
    height: 100dvh;

    @supports not (height: 100dvh) {
      height: -webkit-fill-available; /* older iOS fallback */
    }
    background: ${({ withImageBackground }) => withImageBackground ? `url(${getStaticUrl("/images/onboarding/slide-grid.jpg")}) no-repeat top right` : "white"};
    background-size: ${({ withImageBackground }) => withImageBackground ? "962px 1198px" : "unset"};
    display: flex;
    justify-content: center;
    align-items: center;
`;

/**
 * Migration logic to replace deprecated price ids with the new ones as
 * we still may have marketing links that point to deprecated prices
 * @param plan - The plan to migrate the price ids for.
 * @param priceIds - The price ids to migrate.
 * @returns The migrated price ids.
 */
function migrateDeprecatedPriceIds(plan: WorkspacePlanTier, priceIds: string[]) {
    const plans = stripeProducts[plan].plans as RecurringPlanDefinition[];

    const prices = plans.filter(p => priceIds.includes(p.planId));
    for (const idx in prices) {
        const price = prices[idx];
        if (price.isDeprecated) {
            logger.warn(`migrateDeprecatedPriceIds() found deprecated price id ${price.planId}, will use ${price.migrateTo}`);
            prices[idx] = plans.find(p => p.planId === price.migrateTo);
        }
    }

    return prices.map(p => p.planId);
}

export function getCheckoutPropsFromUrl(location?: Location): CheckoutProps | null {
    const searchParams = new URLSearchParams((location ?? window.location).search);

    const hasCheckoutProps = searchParams.has("plan") ||
        searchParams.has("enterpriseInviteId") ||
        searchParams.has("billingTerm") ||
        searchParams.has("displayName");

    if (!hasCheckoutProps) {
        return null;
    }

    const checkoutProps: CheckoutProps = {
        ...(searchParams.has("plan") ? { plan: searchParams.get("plan") as WorkspacePlanTier } : {}),
        ...(searchParams.has("billingTerm") ? { billingInterval: searchParams.get("billingTerm") as "month" | "year" } : {}),
        ...(searchParams.has("displayName") ? { displayName: searchParams.get("displayName") } : {}),
        ...(searchParams.has("price") ? { priceIds: (searchParams.get("price") ?? "").split(" ").map(id => id?.trim()).filter(Boolean) } : {}),
        ...(searchParams.has("quantity") ? { minQuantity: parseInt(searchParams.get("quantity")) } : {}),
        ...(searchParams.has("workspaceId") ? { workspaceId: searchParams.get("workspaceId") } : {}),
        ...(searchParams.has("enterpriseInviteId") ? { enterpriseInviteId: searchParams.get("enterpriseInviteId") } : {}),
    };

    if (!checkoutProps.plan && checkoutProps.displayName) {
        const planAssumedFromDisplayName = checkoutProps.displayName.toLowerCase().includes("pro") ? WorkspacePlanTier.PRO : WorkspacePlanTier.TEAM;
        checkoutProps.plan = planAssumedFromDisplayName;
    }
    if (!checkoutProps.plan && checkoutProps.enterpriseInviteId) {
        checkoutProps.plan = WorkspacePlanTier.ENTERPRISE;
    }

    if (checkoutProps.plan && checkoutProps.priceIds) {
        checkoutProps.priceIds = migrateDeprecatedPriceIds(checkoutProps.plan, checkoutProps.priceIds);
    }

    return checkoutProps;
}
